import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { EASE } from "./Reveal";

const LINKS = [
  { id: "home", label: "HOME" },
  { id: "heroes", label: "HEROES" },
  { id: "worlds", label: "WORLDS" },
  { id: "books", label: "BOOKS" },
  { id: "timeline", label: "TIMELINE" },
  { id: "upcoming", label: "UPCOMING" },
  { id: "trailer", label: "TRAILER" },
  { id: "creator", label: "CREATOR" },
  { id: "contact", label: "CONTACT" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 350 : 0);
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 3.4, duration: 1.2, ease: EASE }}
        className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-10 py-4 transition-colors duration-700 ${
          scrolled ? "bg-[#030305]/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
        }`}
      >
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="relative block h-7 md:h-8 transition-opacity duration-500 hover:opacity-80"
          aria-label="Cosmic Dreams — home"
        >
          <img
            src="/assets/cosmic-dreams-wordmark-bw.png"
            alt="COSMIC DREAMS"
            data-testid="nav-logo-bw"
            className={`h-full w-auto transition-opacity duration-1000 ${scrolled ? "opacity-0" : "opacity-100"}`}
          />
          <img
            src="/assets/cosmic-dreams-wordmark.png"
            alt=""
            aria-hidden="true"
            data-testid="nav-logo-cyan"
            className={`absolute left-0 top-0 h-full w-auto transition-opacity duration-1000 ${scrolled ? "opacity-100" : "opacity-0"}`}
          />
        </button>
        <div className="hidden xl:flex items-center gap-5">
          {LINKS.slice(1).map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative py-1 font-mono text-[11px] tracking-[0.18em] text-[#A0A0B0] transition-all duration-500 hover:scale-105 hover:text-[#D4AF37]"
            >
              {l.label}
              <span className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-[#D4AF37] transition-transform duration-500 ease-out group-hover:scale-x-100" />
            </button>
          ))}
        </div>
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(true)}
          className="xl:hidden text-[#F5F5FA] hover:text-[#D4AF37] transition-colors"
          aria-label="Open menu"
        >
          <Menu size={22} strokeWidth={1.25} />
        </button>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            data-lenis-prevent
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="fixed inset-0 z-[90] flex flex-col bg-[#030305]/97 backdrop-blur-2xl"
            style={{ height: "100dvh" }}
          >
            <div
              className="flex shrink-0 items-center justify-between px-6 py-4"
              style={{ paddingTop: "calc(1rem + env(safe-area-inset-top))" }}
            >
              <img src="/assets/cosmic-dreams-wordmark.png" alt="COSMIC DREAMS" className="h-6 w-auto" />
              <button
                data-testid="nav-mobile-close"
                onClick={() => setOpen(false)}
                className="text-[#F5F5FA] hover:text-[#D4AF37] transition-colors"
                aria-label="Close menu"
              >
                <X size={24} strokeWidth={1.25} />
              </button>
            </div>
            <div
              data-testid="nav-mobile-scroll"
              className="flex-1 overflow-y-auto overscroll-contain"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <div className="flex min-h-full flex-col items-start justify-center gap-1 px-8 py-6">
                {LINKS.map((l, i) => (
                  <div key={l.id} className="overflow-hidden">
                    <motion.button
                      data-testid={`nav-mobile-link-${l.id}`}
                      initial={{ y: "110%" }}
                      animate={{ y: 0 }}
                      exit={{ y: "110%" }}
                      transition={{ delay: 0.08 * i, duration: 0.7, ease: EASE }}
                      onClick={() => go(l.id)}
                      className="flex items-baseline gap-4 py-1 text-3xl sm:text-4xl tracking-wider text-[#F5F5FA]/80 hover:text-[#D4AF37] transition-colors duration-500"
                    >
                      <span className="font-mono text-[10px] text-[#D4AF37]/60">{String(i + 1).padStart(2, "0")}</span>
                      {l.label}
                    </motion.button>
                  </div>
                ))}
              </div>
              <p
                className="px-8 font-mono text-[9px] tracking-[0.4em] text-[#606070] uppercase"
                style={{ paddingBottom: "calc(5rem + env(safe-area-inset-bottom))" }}
              >
                Dream beyond reality
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
